import type { ReactNode } from "react";
import type { Severity } from "../lib/api";
import type { StatusTone } from "./ui";

/**
 * Shared display primitives for the Phase 3 (blueprints / snapshots / dry-run /
 * simulator) surfaces. These are presentational only — no data fetching — so
 * they can be unit-tested in isolation with mock data.
 */

// ── State separation ─────────────────────────────────────────────────────────
// ODIN must never blur three distinct states. Each has its own label + colour so
// the operator always knows which world they are looking at.
export type WorldState = "current" | "desired" | "proposed";

const WORLD_META: Record<WorldState, { label: string; hint: string }> = {
  current: { label: "CURRENT — live Discord", hint: "What exists in the server right now" },
  desired: { label: "DESIRED — blueprint", hint: "What the blueprint declares" },
  proposed: { label: "PROPOSED — dry-run changes", hint: "What a deploy would change (not applied)" },
};

export function StateTag({ state }: { state: WorldState }): JSX.Element {
  const meta = WORLD_META[state];
  return (
    <span className={`state-tag state-tag--${state}`} title={meta.hint}>
      {meta.label}
    </span>
  );
}

/** A titled panel dedicated to exactly one world-state, colour-keyed to it. */
export function StatePanel({
  state,
  children,
  actions,
}: {
  state: WorldState;
  children?: ReactNode;
  actions?: ReactNode;
}): JSX.Element {
  const meta = WORLD_META[state];
  return (
    <section className={`world-panel world-panel--${state}`}>
      <header className="world-panel__head">
        <div>
          <StateTag state={state} />
          <p className="world-panel__hint">{meta.hint}</p>
        </div>
        {actions ? <div className="row">{actions}</div> : null}
      </header>
      <div className="world-panel__body">{children}</div>
    </section>
  );
}

// ── Severity ─────────────────────────────────────────────────────────────────
export function severityTone(severity: Severity | string): StatusTone {
  switch (severity) {
    case "BLOCKER":
    case "ERROR":
      return "danger";
    case "WARNING":
      return "warn";
    case "INFO":
      return "neutral";
    default:
      return "neutral";
  }
}

export function SeverityBadge({ severity }: { severity: Severity | string }): JSX.Element {
  return <span className={`sev sev--${severityTone(severity)}`}>{severity}</span>;
}

// ── Small helpers ────────────────────────────────────────────────────────────
export function shortHash(hash: string | null | undefined, len = 12): string {
  if (!hash) return "—";
  return hash.length > len ? `${hash.slice(0, len)}…` : hash;
}

export function ageMs(iso: string | null | undefined, nowMs = Date.now()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : nowMs - t;
}

export function formatAge(iso: string | null | undefined, nowMs = Date.now()): string {
  const ms = ageMs(iso, nowMs);
  if (ms === null) return "unknown";
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

/** Key/value definition row used across summaries. */
export function KeyVal({ k, v, mono }: { k: string; v: ReactNode; mono?: boolean }): JSX.Element {
  return (
    <div className="kv">
      <span className="kv__k">{k}</span>
      <span className={mono ? "kv__v kv__v--mono" : "kv__v"}>{v}</span>
    </div>
  );
}

/** A count chip, e.g. "Create 4". */
export function CountChip({ label, value, tone = "neutral" }: { label: string; value: number; tone?: StatusTone }): JSX.Element {
  return (
    <span className={`count-chip count-chip--${tone}`}>
      <strong>{value}</strong> {label}
    </span>
  );
}

/**
 * A collapsed disclosure for technical / advanced detail. Plain-language content
 * stays visible; technical bitfields and raw JSON hide behind this until asked.
 */
export function Disclosure({ summary, children }: { summary: string; children: ReactNode }): JSX.Element {
  return (
    <details className="disclosure">
      <summary>{summary}</summary>
      <div className="disclosure__body">{children}</div>
    </details>
  );
}

/**
 * Read-only "Advanced" JSON view. Explicitly labelled and never a deploy
 * control — it exists to show schema context, not to edit or apply anything.
 */
export function AdvancedJson({ title = "Advanced — read-only JSON", value }: { title?: string; value: unknown }): JSX.Element {
  return (
    <Disclosure summary={title}>
      <pre className="json-view" aria-label={title}>
        {JSON.stringify(value, null, 2)}
      </pre>
    </Disclosure>
  );
}
